import React from "react";
import { BookOpen, GraduationCap, Lightbulb, TrendingUp, type LucideIcon } from "lucide-react";
import { Heading } from "../../components/ui/Heading";
import { BodyText } from "../../components/ui/BodyText";
import { BulletList } from "../../components/ui/BulletList";

export interface BlogArticle {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  icon: LucideIcon;
  image: string;
  author: string;
  publishDate: string;
  content: React.ReactNode;
}

export const blogArticles: BlogArticle[] = [
  {
    slug: "standout-academic-cv-2025",
    title: "How to Write a Standout Academic CV in 2025",
    excerpt:
      "Your academic CV is your first impression. Learn how to highlight research output, teaching experience, and professional development effectively.",
    category: "Career Tips",
    readTime: "6 min read",
    icon: BookOpen,
    image: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&h=450&fit=crop",
    author: "Acanetic Editorial Team",
    publishDate: "15 January 2025",
    content: (
      <>
        <BodyText className="mb-6">
          In UK higher education, your academic CV is far more than a summary of qualifications — it is the document that determines whether you are shortlisted for interview. With competition for lectureships, readerships, and professorships at an all-time high, a well-structured CV can be the difference between progressing to the next stage and being overlooked entirely.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          1. Structure Your CV for UK Academic Conventions
        </Heading>
        <BodyText className="mb-4">
          Unlike industry CVs, academic CVs in the UK follow a specific structure that panels expect. Stick to this order for maximum clarity:
        </BodyText>
        <BulletList
          marker="disc"
          className="mb-6"
          items={[
            "Personal details and contact information",
            "Education and qualifications (including PhD thesis title and supervisors)",
            "Academic appointments and employment history",
            "Research interests and expertise",
            "Publications (peer-reviewed articles, book chapters, monographs)",
            "Research grants and funding (with amounts in GBP and funders such as UKRI, Wellcome, Leverhulme)",
            "Teaching experience and qualifications (including HEA Fellowship status)",
            "Supervision of postgraduate researchers",
            "Administrative and leadership roles",
            "Professional memberships and service",
            "Conference presentations and invited talks",
            "References",
          ]}
        />

        <Heading level={3} className="mb-4 mt-8">
          2. Foreground Your Research Impact
        </Heading>
        <BodyText className="mb-4">
          UK universities are driven by the Research Excellence Framework (REF). When listing publications, highlight outputs that have been submitted to REF or that demonstrate international significance. Include citation counts where strong, and note any outputs rated as world-leading (4*) or internationally excellent (3*) in previous REF exercises.
        </BodyText>
        <BodyText className="mb-6">
          List grants with the full monetary value in GBP, the funding body (UKRI, EPSRC, AHRC, British Academy, etc.), your role (PI, Co-I, or Co-PI), and the project duration. Panels want to see that you can win competitive funding.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          3. Demonstrate Teaching Excellence
        </Heading>
        <BodyText className="mb-4">
          With the Teaching Excellence Framework (TEF) now embedded in UK higher education, teaching is no longer secondary. Include:
        </BodyText>
        <BulletList
          marker="check"
          className="mb-6"
          items={[
            "Modules designed and delivered, with student numbers",
            "Student evaluation scores or NSS-related feedback",
            "HEA Fellowship level (Associate, Fellow, Senior Fellow, Principal Fellow)",
            "Any teaching awards or commendations",
            "Innovations in pedagogy, such as blended or flipped classroom approaches",
          ]}
        />

        <Heading level={3} className="mb-4 mt-8">
          4. Tailor Your CV to Each Application
        </Heading>
        <BodyText className="mb-4">
          A generic CV will not cut through. For each role, read the job description and person specification carefully. Reorder sections or add brief contextual notes to show alignment. For example, if a role emphasises interdisciplinary research, bring collaborative projects and cross-departmental work to the fore.
        </BodyText>
        <BodyText className="mb-6">
          If applying to a Russell Group institution with a strong research focus, lead with publications and grants. For a post-1992 university with a teaching-intensive profile, foreground your pedagogical experience and student outcomes.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          5. Formatting and Length
        </Heading>
        <BodyText className="mb-4">
          There is no strict page limit for UK academic CVs — unlike industry roles, brevity is less important than completeness. However, clarity matters:
        </BodyText>
        <BulletList
          marker="disc"
          className="mb-6"
          items={[
            "Use clear headings and consistent formatting throughout",
            "List publications in reverse chronological order using a standard citation style",
            "Include DOIs or links for key publications",
            "Use a professional, readable font (11–12pt) with generous margins",
            "Save as PDF to preserve formatting across devices",
          ]}
        />

        <Heading level={3} className="mb-4 mt-8">
          6. Common Mistakes to Avoid
        </Heading>
        <BulletList
          marker="dash"
          className="mb-6"
          items={[
            "Listing publications without distinguishing peer-reviewed from non-peer-reviewed work",
            "Omitting grant values — panels need to see the scale of funding you have secured",
            "Using an industry-style two-page CV for an academic role",
            "Failing to include your HEA Fellowship status or equivalent teaching qualification",
            "Neglecting to mention PhD supervision completions and current supervisees",
          ]}
        />

        <BodyText className="mb-4">
          Your academic CV is a living document that evolves with your career. Keep it updated after every publication, grant award, or new teaching responsibility. In a competitive UK market, the candidates who present their achievements most clearly — not necessarily those with the longest CVs — are the ones who get shortlisted.
        </BodyText>
      </>
    ),
  },
  {
    slug: "hybrid-teaching-roles-uk-universities",
    title: "The Rise of Hybrid Teaching Roles in UK Universities",
    excerpt:
      "Flexible and hybrid academic positions are growing rapidly. Discover what this means for tutors, lecturers, and institutions.",
    category: "Industry Trends",
    readTime: "5 min read",
    icon: TrendingUp,
    image: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&h=450&fit=crop",
    author: "Acanetic Editorial Team",
    publishDate: "22 January 2025",
    content: (
      <>
        <BodyText className="mb-6">
          The landscape of academic employment in the UK is shifting. Since the pandemic accelerated the adoption of online and blended learning, universities have increasingly created roles that combine in-person and remote delivery. These hybrid teaching positions are reshaping how institutions recruit, how academics work, and how students learn.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          What Are Hybrid Teaching Roles?
        </Heading>
        <BodyText className="mb-4">
          Hybrid teaching roles typically involve a combination of face-to-face lectures, online seminars, asynchronous content creation, and virtual office hours. Unlike fully remote positions — which remain rare in UK academia — hybrid roles require regular campus presence alongside significant digital delivery.
        </BodyText>
        <BodyText className="mb-6">
          These roles go by various titles: Lecturer in Digital Education, Blended Learning Tutor, Online Programme Lead, or simply Lecturer with a stated hybrid delivery expectation in the job description.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Why Are UK Universities Adopting Hybrid Models?
        </Heading>
        <BulletList
          marker="check"
          className="mb-6"
          items={[
            "Student demand: HESA data shows that students increasingly expect flexible access to learning materials, recorded lectures, and online support alongside traditional contact hours",
            "Widening participation: Hybrid delivery helps institutions reach mature students, part-time learners, and those with caring responsibilities who cannot attend campus full-time",
            "Cost efficiency: Reducing the need for large lecture theatres and enabling shared teaching across campuses or partner institutions",
            "Global reach: UK universities competing for international students can offer hybrid programmes that reduce visa and relocation barriers for the initial stages of study",
          ]}
        />

        <Heading level={3} className="mb-4 mt-8">
          What This Means for Academic Job Seekers
        </Heading>
        <BodyText className="mb-4">
          If you are applying for teaching-focused roles in UK higher education, expect hybrid competence to be assessed. Key skills that panels now look for include:
        </BodyText>
        <BulletList
          marker="disc"
          className="mb-6"
          items={[
            "Experience with virtual learning environments (VLEs) such as Moodle, Blackboard, or Canvas",
            "Ability to design engaging asynchronous content (recorded lectures, interactive quizzes, discussion forums)",
            "Familiarity with accessibility standards for digital learning materials",
            "Evidence of adapting assessment methods for online delivery",
            "Understanding of digital pedagogy principles and evidence-based approaches to online engagement",
          ]}
        />

        <Heading level={3} className="mb-4 mt-8">
          Implications for Institutions
        </Heading>
        <BodyText className="mb-4">
          For universities, the shift to hybrid creates both opportunities and challenges. On the recruitment side, hybrid roles can attract a wider talent pool — academics who might not relocate for a fully campus-based position may consider a role with two or three days per week on site.
        </BodyText>
        <BodyText className="mb-6">
          However, institutions must invest in training, technology infrastructure, and clear policies around workload allocation for hybrid delivery. The UCU has raised concerns about the potential for hybrid expectations to increase workload without corresponding recognition in promotion criteria.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          The Salary Picture
        </Heading>
        <BodyText className="mb-4">
          Hybrid teaching roles typically sit on the same national pay spine as equivalent campus-based positions. Lecturers can expect salaries in the range of £38,000–£56,000, with senior lecturers earning £48,000–£62,000, depending on the institution and region. London weighting of approximately £3,000–£5,000 applies where relevant.
        </BodyText>
        <BodyText className="mb-6">
          The real differentiator for hybrid roles is often flexibility rather than pay — the ability to structure working weeks around teaching commitments, reducing commuting time and enabling better work-life balance.
        </BodyText>

        <BodyText>
          Hybrid teaching is not a temporary response to the pandemic — it is becoming a permanent feature of UK higher education. Academics who develop strong digital teaching skills alongside traditional expertise will find themselves well-positioned in an evolving job market.
        </BodyText>
      </>
    ),
  },
  {
    slug: "interview-tips-academic-positions",
    title: "Top 10 Interview Tips for Academic Positions",
    excerpt:
      "From research presentations to panel interviews, prepare for every stage of the academic hiring process with these proven strategies.",
    category: "Career Tips",
    readTime: "8 min read",
    icon: Lightbulb,
    image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800&h=450&fit=crop",
    author: "Acanetic Editorial Team",
    publishDate: "5 February 2025",
    content: (
      <>
        <BodyText className="mb-6">
          Academic interviews in the UK are unlike any other hiring process. They typically involve a presentation, a panel interview, and sometimes a teaching demonstration or informal meetings with the department — all in a single day. Here are ten strategies to help you perform at your best.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          1. Understand the Format Before the Day
        </Heading>
        <BodyText className="mb-6">
          Most UK universities will send you an interview schedule in advance. This usually includes a 15–20 minute research presentation followed by a panel interview of 45–60 minutes. Some institutions also include a teaching demonstration, a meeting with postgraduate researchers, or an informal lunch with the department. Know what to expect so you can prepare appropriately for each element.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          2. Tailor Your Presentation to the Audience
        </Heading>
        <BodyText className="mb-6">
          Your research presentation will be assessed by a panel that may include academics from outside your specialism, HR representatives, and sometimes an external assessor. Avoid jargon-heavy slides that only specialists would follow. Open with the big question your research addresses, explain why it matters, present key findings clearly, and close with future directions — ideally ones that align with the department's research strengths.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          3. Know the Department Inside Out
        </Heading>
        <BodyText className="mb-4">
          Before the interview, thoroughly research:
        </BodyText>
        <BulletList
          marker="disc"
          className="mb-6"
          items={[
            "The department's research groups, centres, and clusters — and how your work connects",
            "Recent REF results for the unit of assessment you would be submitted to",
            "Current degree programmes and modules you could contribute to",
            "The institution's strategic plan, especially priorities around research, teaching, or interdisciplinarity",
            "Recent news — new hires, grant successes, or partnerships",
          ]}
        />

        <Heading level={3} className="mb-4 mt-8">
          4. Prepare for the 'Five-Year Plan' Question
        </Heading>
        <BodyText className="mb-6">
          Panels almost always ask where you see your research going over the next three to five years. Have a clear, realistic answer that includes specific funding targets (name the funders — UKRI councils, Leverhulme, British Academy), planned outputs, potential collaborations within the department, and any plans for postgraduate supervision. This demonstrates that you have thought beyond the immediate appointment.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          5. Demonstrate Teaching Commitment
        </Heading>
        <BodyText className="mb-6">
          Even for research-focused roles, UK panels expect evidence of teaching capability and enthusiasm. Be ready to discuss modules you could design, your approach to inclusive pedagogy, your HEA Fellowship status (or plans to achieve it), and how you would contribute to the department's TEF performance. If asked to deliver a teaching demonstration, treat it as seriously as the research presentation.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          6. Show Awareness of the Wider University
        </Heading>
        <BodyText className="mb-6">
          Academic roles involve citizenship — service on committees, engagement with widening participation, public engagement, and administrative duties. Mention relevant experience: external examining, journal reviewing, conference organisation, or roles within learned societies. Panels want to know you will be a collegiate member of the department.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          7. Prepare Thoughtful Questions
        </Heading>
        <BodyText className="mb-4">
          You will usually be invited to ask questions at the end. Avoid asking about information freely available on the website. Instead, ask about:
        </BodyText>
        <BulletList
          marker="disc"
          className="mb-6"
          items={[
            "Research mentoring or support for new staff",
            "Plans for the next REF cycle and how the department is preparing",
            "Opportunities for interdisciplinary collaboration",
            "The teaching workload model and how it is managed",
            "Start-up funding or laboratory/resource provisions for new appointees",
          ]}
        />

        <Heading level={3} className="mb-4 mt-8">
          8. Handle the Teaching Demonstration Well
        </Heading>
        <BodyText className="mb-6">
          If a teaching demonstration is part of the day, you will typically be given a topic and a notional student level (e.g., second-year undergraduates). Plan an interactive session, not a mini-lecture. Ask questions, include a brief activity, and show that you can engage learners. Keep to time — going over is a common mistake that panels note.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          9. Be Authentic and Collegiate
        </Heading>
        <BodyText className="mb-6">
          Academic panels are hiring a future colleague, not just a researcher. Be genuine, listen carefully to questions, and do not oversell your achievements. If you do not know the answer to a question, say so honestly and explain how you would approach finding out. Arrogance is noticed and remembered; intellectual humility is valued.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          10. Follow Up Professionally
        </Heading>
        <BodyText className="mb-6">
          After the interview, a brief thank-you email to the panel chair is courteous but not obligatory. If you are unsuccessful, ask for feedback — UK universities are generally good at providing this, and it can be invaluable for future applications. Each interview is a learning experience, and many academics are appointed only after several attempts.
        </BodyText>

        <BodyText>
          The UK academic interview process can feel daunting, but thorough preparation and genuine engagement will serve you well. Remember that the panel wants you to succeed — they have shortlisted you because your application showed promise. Your task on the day is simply to bring that promise to life.
        </BodyText>
      </>
    ),
  },
  {
    slug: "uk-academic-job-market-2025",
    title: "Understanding the UK Academic Job Market: A 2025 Overview",
    excerpt:
      "An in-depth look at hiring trends, salary benchmarks, and the most in-demand disciplines across UK higher education.",
    category: "Industry Trends",
    readTime: "4 min read",
    icon: GraduationCap,
    image: "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=800&h=450&fit=crop",
    author: "Acanetic Editorial Team",
    publishDate: "12 February 2025",
    content: (
      <>
        <BodyText className="mb-6">
          The UK academic job market in 2025 is shaped by funding pressures, shifting student demographics, and evolving research priorities. Whether you are a postdoctoral researcher seeking your first permanent post or an institution planning your next round of recruitment, understanding these dynamics is essential.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          The Current Landscape
        </Heading>
        <BodyText className="mb-4">
          HESA data indicates that UK higher education employs approximately 225,000 academic staff across 160+ institutions. However, the distribution of roles is uneven. Russell Group universities account for a disproportionate share of research-focused positions, while post-1992 institutions employ the majority of teaching-focused staff.
        </BodyText>
        <BodyText className="mb-6">
          The sector faces a well-documented tension: rising student numbers (particularly in subjects like computer science, business, and healthcare) alongside constrained funding from a domestic tuition fee that has been frozen at £9,250 since 2017. International student fees and research grants remain critical revenue streams.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Disciplines in Demand
        </Heading>
        <BodyText className="mb-4">
          Recruitment patterns vary significantly by discipline. The strongest demand in 2025 is in:
        </BodyText>
        <BulletList
          marker="check"
          className="mb-6"
          items={[
            "Computer Science and AI: driven by student demand and government investment in digital skills. Many departments are expanding rapidly, creating opportunities at all levels from lecturer to professor",
            "Nursing, Allied Health, and Medical Sciences: NHS workforce shortages are driving expansion of healthcare programmes, with universities recruiting clinical academics and practice-based educators",
            "Engineering: particularly in areas aligned with the government's net-zero agenda — renewable energy, sustainable materials, and environmental engineering",
            "Business and Management: consistently high student numbers, with particular demand for academics with industry experience alongside research credentials",
            "Psychology: one of the most popular undergraduate subjects in the UK, creating sustained demand for teaching-focused and research-active staff",
          ]}
        />
        <BodyText className="mb-6">
          Conversely, some humanities and social science departments face contraction, with fewer permanent posts being advertised and a greater reliance on fixed-term or hourly-paid staff.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Salary Benchmarks
        </Heading>
        <BodyText className="mb-4">
          UK academic salaries are largely determined by the national pay spine, negotiated through the Universities and Colleges Employers Association (UCEA). As of 2025, typical ranges are:
        </BodyText>
        <BulletList
          marker="disc"
          className="mb-6"
          items={[
            "Postdoctoral Research Associate: £35,000–£42,000",
            "Lecturer (Grade 7/8): £38,000–£56,000",
            "Senior Lecturer / Reader: £48,000–£62,000",
            "Professor: £65,000–£100,000+ (with significant variation by institution and discipline)",
          ]}
        />
        <BodyText className="mb-6">
          London weighting adds approximately £3,000–£5,000 to these figures. Some institutions, particularly in business schools and STEM disciplines, offer market supplements of £5,000–£15,000 to compete with industry salaries.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          The Fixed-Term Problem
        </Heading>
        <BodyText className="mb-4">
          One of the most persistent challenges in UK academia is the prevalence of fixed-term contracts. UCU data suggests that around 70% of research-only staff are on fixed-term contracts, and many early-career academics cycle through multiple postdoctoral positions before securing a permanent role — if they do at all.
        </BodyText>
        <BodyText className="mb-6">
          Some progress has been made: the Researcher Development Concordat encourages institutions to improve career support for researchers, and a growing number of universities have committed to reducing precarious employment. However, structural change remains slow.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          The Impact of Research Funding
        </Heading>
        <BodyText className="mb-4">
          UKRI remains the largest public funder of research in the UK, with a total budget of approximately £8.8 billion in 2025–26. That budget is distributed across nine constituent bodies rather than one pot: the seven research councils, plus Research England, which channels quality-related funding to universities in England, and Innovate UK, which funds business-led innovation. Only the research council share is available as competitive grant funding to an individual academic. Funding trends shape hiring — when a research council prioritises a particular area (such as AI, clean energy, or pandemic preparedness), universities respond by recruiting academics who can win grants in those areas.
        </BodyText>
        <BodyText className="mb-6">
          The post-Brexit landscape has also affected research funding. While the UK has associated to Horizon Europe, the uncertainty of the transition period led some institutions to focus on domestic funding streams. Academics with a track record of securing funding from multiple sources are particularly attractive to institutions.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          What Scholars Should Know
        </Heading>
        <BulletList
          marker="check"
          className="mb-6"
          items={[
            "Timing matters: the main recruitment cycle for permanent posts runs from January to June, with September start dates. However, positions are advertised year-round",
            "Look beyond Russell Group: post-1992 universities and specialist institutions often offer excellent career development, manageable workloads, and genuine teaching innovation",
            "Network actively: many positions are shaped by existing relationships — attend conferences, engage with research groups, and build your profile within your discipline",
            "Consider Scotland and Wales: devolved funding and different fee structures mean that Scottish and Welsh institutions sometimes have more financial flexibility for new appointments",
            "Use specialist platforms: general job boards miss many academic listings — platforms like Acanetic, jobs.ac.uk, and institutional career pages are essential",
          ]}
        />

        <BodyText>
          The UK academic job market in 2025 is competitive but far from static. Those who understand the landscape, position themselves strategically, and bring a combination of research excellence, teaching commitment, and collegiality will find opportunities — even in a challenging environment.
        </BodyText>
      </>
    ),
  },
  {
    slug: "attract-top-academic-talent",
    title: "How Institutions Can Attract Top Academic Talent",
    excerpt:
      "Competitive packages, research funding, and culture matter. Here's what today's academic professionals are looking for in an institution.",
    category: "For Institutions",
    readTime: "4 min read",
    icon: Lightbulb,
    image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&h=450&fit=crop",
    author: "Acanetic Editorial Team",
    publishDate: "20 February 2025",
    content: (
      <>
        <BodyText className="mb-6">
          In a competitive market, the most talented academics have choices. Universities that rely solely on prestige or the promise of an academic title will find themselves losing scholars to institutions that offer tangible support, genuine career development, and a healthy working culture. Here is what matters most to academic professionals in 2025.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          1. Competitive and Transparent Remuneration
        </Heading>
        <BodyText className="mb-4">
          The national pay spine provides a framework, but top scholars compare total packages. Institutions that attract the best talent are transparent about:
        </BodyText>
        <BulletList
          marker="disc"
          className="mb-6"
          items={[
            "Starting salary and progression increments",
            "Market supplements for high-demand disciplines (common in business, computer science, and engineering)",
            "Relocation allowances — particularly important for international hires",
            "Pension arrangements (USS for pre-1992, TPS or LGPS for post-1992 universities)",
            "Childcare support — typically a workplace nursery place taken via salary sacrifice, with Tax-Free Childcare signposted for staff who claim it themselves",
            "Cycle-to-work schemes and other benefits",
          ]}
        />
        <BodyText className="mb-6">
          One caution on the nursery benefit: the tax exemption depends on the institution meeting HMRC's partnership requirements and carrying genuine financial and management risk in the nursery, and HMRC has been challenging third-party marketed schemes that do not. University-run nurseries of the kind Oxford and Cambridge operate are the safe pattern.
        </BodyText>
        <BodyText className="mb-6">
          Advertising a role as 'competitive salary' without a stated range is increasingly seen as a red flag. Scholars — particularly women and those from underrepresented groups — are more likely to apply when salary information is clear.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          2. Research Support and Start-Up Packages
        </Heading>
        <BodyText className="mb-4">
          For research-active academics, the support infrastructure is often more important than salary. Attractive research support includes:
        </BodyText>
        <BulletList
          marker="check"
          className="mb-6"
          items={[
            "Start-up funding for equipment, travel, and initial research costs (typically £10,000–£50,000 depending on discipline)",
            "Protected research time in the first two to three years, with a reduced teaching load",
            "Access to research support offices that provide grant-writing assistance and costing support",
            "PhD studentship allocations or funding to recruit research students",
            "Mentoring from established academics, including guidance on the REF submission process",
          ]}
        />

        <Heading level={3} className="mb-4 mt-8">
          3. Workload Transparency
        </Heading>
        <BodyText className="mb-4">
          Academic workload is one of the most contentious issues in UK higher education. The best institutions are upfront about their workload allocation model (WAM) and ensure that teaching, research, and administration are balanced fairly. Red flags for scholars include:
        </BodyText>
        <BulletList
          marker="dash"
          className="mb-6"
          items={[
            "No workload model at all, or one that exists only on paper",
            "Excessive teaching loads (more than 200 contact hours per year for a research-active post)",
            "Administrative tasks not accounted for in workload planning",
            "An expectation that research happens 'in your own time'",
          ]}
        />

        <Heading level={3} className="mb-4 mt-8">
          4. Career Development and Promotion
        </Heading>
        <BodyText className="mb-6">
          Clear, accessible promotion criteria are essential. Academics want to know what is expected for progression from lecturer to senior lecturer, reader, and professor — and they want those criteria to be applied consistently. Institutions that value teaching alongside research in their promotion frameworks are particularly attractive to scholars with a blended profile. The introduction of teaching-focused professorships at many Russell Group universities has been a positive development.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          5. Equality, Diversity, and Inclusion
        </Heading>
        <BodyText className="mb-4">
          The best scholars assess an institution's EDI credentials carefully. Demonstrable commitment includes:
        </BodyText>
        <BulletList
          marker="check"
          className="mb-6"
          items={[
            "Athena SWAN awards (Bronze, Silver, or Gold) at department and institutional level",
            "Race Equality Charter membership and action",
            "Published gender and ethnicity pay gap data",
            "Family-friendly policies including shared parental leave, flexible working, and career-break support",
            "Active staff networks for LGBTQ+, disabled, and Black, Asian, and minority ethnic colleagues",
          ]}
        />

        <Heading level={3} className="mb-4 mt-8">
          6. Culture and Collegiality
        </Heading>
        <BodyText className="mb-6">
          Culture is harder to quantify but consistently ranks among the top factors in scholar decision-making. Departments known for collegiality, supportive leadership, and a healthy approach to work-life balance attract stronger fields. Conversely, departments with a reputation for internal politics, bullying, or excessive managerialism struggle to recruit — regardless of their research profile.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          7. Write Better Listings
        </Heading>
        <BodyText className="mb-4">
          Finally, the most practical step institutions can take is to improve the quality of their listings. The best academic listings:
        </BodyText>
        <BulletList
          marker="check"
          className="mb-6"
          items={[
            "State the salary range clearly",
            "Describe the role's purpose and how it fits within the department",
            "Distinguish between essential and desirable criteria realistically",
            "Avoid unnecessarily long lists of requirements that discourage diverse scholars from applying",
            "Include information about the department's culture, research strengths, and support provisions",
            "Use inclusive language and avoid gendered or exclusionary phrasing",
          ]}
        />

        <BodyText>
          Attracting top academic talent is not solely about money — it is about creating an environment where excellent people can do their best work. Institutions that invest in research support, workload fairness, career clarity, and genuine inclusion will consistently build the strongest departments.
        </BodyText>
      </>
    ),
  },
  {
    slug: "postdoc-to-permanent-faculty",
    title: "Transitioning from Postdoc to Permanent Faculty",
    excerpt:
      "Practical advice on navigating the jump from fixed-term research positions to permanent lectureship and professorship roles.",
    category: "Career Tips",
    readTime: "4 min read",
    icon: BookOpen,
    image: "https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=800&h=450&fit=crop",
    author: "Acanetic Editorial Team",
    publishDate: "3 March 2025",
    content: (
      <>
        <BodyText className="mb-6">
          The transition from postdoctoral research to a permanent academic post is one of the most challenging steps in an academic career. In the UK, this typically means moving from a fixed-term research associate position (on the Grade 6/7 spine) to a lectureship — a permanent, open-ended role that combines research, teaching, and administration.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Understanding the Gap
        </Heading>
        <BodyText className="mb-4">
          The fundamental challenge is that postdoctoral positions are designed around research, while lectureships require a much broader profile. A strong publication record alone is not sufficient — selection panels also assess:
        </BodyText>
        <BulletList
          marker="disc"
          className="mb-6"
          items={[
            "Independent research vision (distinct from your PhD supervisor's agenda)",
            "Teaching experience and evidence of pedagogical skill",
            "Ability to win competitive funding as a PI",
            "Administrative and service contributions",
            "Potential for postgraduate supervision",
          ]}
        />
        <BodyText className="mb-6">
          Many postdocs are so focused on publications that they arrive at interview without sufficient teaching experience or a credible funding strategy. Planning ahead is essential.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Building Your Teaching Profile
        </Heading>
        <BodyText className="mb-4">
          Most postdoctoral contracts do not include formal teaching responsibilities, but there are ways to build experience:
        </BodyText>
        <BulletList
          marker="check"
          className="mb-6"
          items={[
            "Volunteer to give guest lectures in your department",
            "Offer to supervise undergraduate or Master's dissertation students",
            "Seek opportunities to design and deliver a short module or workshop",
            "Complete your institution's PGCert in Academic Practice or equivalent",
            "Apply for Associate or Fellow status of the Higher Education Academy (HEA)",
          ]}
        />
        <BodyText className="mb-6">
          HEA Fellowship is increasingly expected for lectureship applications, and having it (or being close to completing it) gives you a significant advantage.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Developing an Independent Research Identity
        </Heading>
        <BodyText className="mb-4">
          Panels want to see that your research agenda is your own — not simply an extension of your supervisor's programme. Practical steps include:
        </BodyText>
        <BulletList
          marker="disc"
          className="mb-6"
          items={[
            "Publish as first or sole author on work that is clearly yours",
            "Develop collaborations outside your immediate research group",
            "Apply for fellowships in your own name — Leverhulme Early Career Fellowships and British Academy Postdoctoral Fellowships are both open only to researchers who have not held a permanent academic post",
            "Present your independent research direction at conferences",
            "Write a clear, compelling research statement that articulates your five-year vision",
          ]}
        />

        <Heading level={3} className="mb-4 mt-8">
          The Funding Question
        </Heading>
        <BodyText className="mb-6">
          Demonstrating the ability to attract funding is increasingly important for lectureship appointments, even at the entry level. You do not need to have won a major grant, but you should be able to show engagement with the funding landscape: applications submitted (even if unsuccessful), knowledge of relevant funders (EPSRC, ESRC, AHRC, MRC, BBSRC, NERC, Wellcome, Leverhulme), and a realistic plan for future bids. There is no single UKRI-wide new investigator award — EPSRC, BBSRC, MRC and ESRC each run their own, with different rules — and because all of them are held through a research organisation that backs the application, they generally come with or after a first appointment rather than before one. Some institutions offer bridging support or internal seed funding for new lecturers — ask about this during the interview process.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Timing Your Applications
        </Heading>
        <BodyText className="mb-4">
          There is no perfect moment to apply for lectureships, but consider the following:
        </BodyText>
        <BulletList
          marker="disc"
          className="mb-6"
          items={[
            "The main recruitment window is January to June for September starts, but positions appear year-round",
            "Apply broadly — do not limit yourself to Russell Group or to a single region",
            "Consider teaching-focused lectureships as a route into permanent employment; many academics later develop their research profile once in post",
            "Look at post-1992 universities, where competition may be less intense and the focus on teaching excellence may suit your profile",
            "International institutions with strong UK connections (Ireland, Netherlands, Australia) can also be stepping stones",
          ]}
        />

        <Heading level={3} className="mb-4 mt-8">
          Managing the Emotional Side
        </Heading>
        <BodyText className="mb-6">
          The postdoc-to-faculty transition is not just a professional challenge — it is an emotional one. Rejection is normal; most successful academics applied for many lectureships before being appointed. Seek feedback after unsuccessful interviews, maintain your professional networks, and consider mentoring from academics who have recently made the transition themselves. University HR departments and researcher development teams can also provide support.
        </BodyText>

        <BodyText>
          The transition from postdoc to permanent faculty requires strategic planning, patience, and a willingness to develop skills beyond your research expertise. Start building your teaching and funding profile early, develop a clear independent research identity, and apply widely. The path is challenging, but with the right preparation, it is achievable.
        </BodyText>
      </>
    ),
  },
  {
    slug: "skilled-worker-visa-academic-recruitment",
    title: "The Skilled Worker Visa After the 2025 Rules: What Changed for Academic Hiring",
    excerpt:
      "The going rate for higher education teaching professionals is now £52,600 — above the top of a standard lecturer grade. A guide to the salary options, the postdoctoral new entrant rate, and what sponsorship costs.",
    category: "Industry Trends",
    readTime: "8 min read",
    icon: TrendingUp,
    image: "https://images.unsplash.com/photo-1543165796-5426273eaab3?w=800&h=450&fit=crop",
    author: "Acanetic Editorial Team",
    publishDate: "5 August 2026",
    content: (
      <>
        <BodyText className="mb-6">
          The Skilled Worker route was rewritten on 22 July 2025, and the changes landed unevenly across higher education. Nothing in that package removed academic roles from the route — they were already graduate-level work — but it moved the salary arithmetic far enough that a standard lectureship can now fail a test that a postdoctoral position passes comfortably. What follows is the position as it stands in August 2026, and the parts of the route that academic recruitment actually turns on.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          What changed on 22 July 2025
        </Heading>
        <BodyText className="mb-4">
          Four things took effect on the same day:
        </BodyText>
        <BulletList
          marker="disc"
          className="mb-6"
          items={[
            "The skill threshold rose from RQF 3 to RQF 6 — degree level — removing 111 occupations from the route",
            "The general salary threshold rose from £38,700 to £41,700 a year",
            "The Immigration Salary List, which had allowed a discount against the going rate, was abolished",
            "A Temporary Shortage List was created for occupations below RQF 6, running until at least 31 December 2026",
          ]}
        />
        <BodyText className="mb-6">
          For higher education, the skill threshold was a non-event and the abolition of the Immigration Salary List was not. Academic posts already sat at or above degree level, so the RQF 6 change removed nothing from the sector. The salary changes did the work, and they did it in a way that is easy to misread if you only look at the headline figure.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          The going rate, not the general threshold, is the binding number
        </Heading>
        <BodyText className="mb-4">
          Sponsorship requires a salary that clears two separate tests: the general threshold of £41,700, and the going rate for the occupation code the role is assigned to. The higher of the two governs. For most academic posts the going rate is the higher figure, and by a wide margin.
        </BodyText>
        <BodyText className="mb-4">
          The going rate for SOC 2311, higher education teaching professionals, is £52,600 a year — £26.97 an hour — under Table 1 of Appendix Skilled Occupations, effective 8 April 2026. Table 1 is the median-earnings table, and it is the one that applies to sponsorships assigned under the current rules.
        </BodyText>
        <BodyText className="mb-6">
          Set that against the national pay spine. On the New JNCHES spine values effective 1 August 2025, spine point 28 pays £37,694 and spine point 34 pays £47,389 — a range that covers a typical Grade 7 lectureship at many institutions. The standard salary option therefore requires more than the entire grade a new lecturer would normally be appointed to, and more than the bottom half of the grade above it. Institutions that assumed £41,700 was the number to beat have been reading the wrong line.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          The occupation code decides the number
        </Heading>
        <BodyText className="mb-4">
          Because the going rate is set per occupation code, the code a role is assigned to changes the salary requirement more than the seniority of the role does. The Table 1 rates for the codes academic recruitment commonly uses are:
        </BodyText>
        <BulletList
          marker="disc"
          className="mb-6"
          items={[
            "2311, higher education teaching professionals — £52,600",
            "2114, physical scientists — £54,600",
            "2113, biochemists and biomedical scientists — £45,900",
            "2162, other researchers, unspecified discipline — £43,600",
            "2119, natural and social science professionals not elsewhere classified — £41,500",
            "2112, biological scientists — £40,300",
            "2111, chemical scientists — £39,900",
          ]}
        />
        <BodyText className="mb-6">
          The spread is £14,700 between the lowest and highest of these, for roles that may sit at the same grade and spine point. A postdoctoral position coded 2112 carries a going rate of £40,300; a lectureship coded 2311 carries £52,600. Codes are assigned according to the actual duties of the role rather than chosen for convenience, and assigning one incorrectly is a compliance problem rather than a saving. But where a role genuinely could be described either way, the code is a decision with a five-figure consequence, and it is made by the institution rather than by the scholar.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          The four salary options that apply to academic posts
        </Heading>
        <BodyText className="mb-4">
          The route provides discounted options. Each sets a cash floor and a percentage of the going rate, and again the higher figure governs. Applied to the £52,600 going rate for SOC 2311, the options work out as follows:
        </BodyText>
        <BulletList
          marker="disc"
          className="mb-6"
          items={[
            "Option A, standard: the higher of £41,700 or 100% of the going rate — £52,600",
            "Option B, relevant PhD outside STEM: the higher of £37,500 or 90% of the going rate — £47,340",
            "Option C, relevant STEM PhD: the higher of £33,400 or 80% of the going rate — £42,080",
            "Option E, new entrant: the higher of £33,400 or 70% of the going rate — £36,820",
          ]}
        />
        <BodyText className="mb-6">
          The arithmetic is worth checking rather than taking on trust, because the practical consequence is sharp. A Grade 7 lectureship starting at £37,694 clears the new entrant option and nothing else. The same post clears the STEM PhD option only once it reaches roughly spine point 31. The non-STEM PhD option lands at £47,340, within about £50 of the top of the grade. Which option a scholar qualifies for, not the salary attached to the listing, is usually what decides whether a sponsorship works.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Who counts as a new entrant
        </Heading>
        <BodyText className="mb-4">
          The new entrant option is the one that makes most entry-level academic sponsorship viable, and its definition is broader than the name suggests. A scholar qualifies by meeting either of these:
        </BodyText>
        <BulletList
          marker="check"
          className="mb-6"
          items={[
            "Being under the age of 26 on the date of application",
            "Being sponsored for a postdoctoral position in one of the specified SOC 2020 occupation codes — no age limit attaches to this route",
          ]}
        />
        <BodyText className="mb-6">
          The postdoctoral limb matters because it decouples the discount from age entirely. The constraint is time, not seniority: a scholar can be treated as a new entrant for a maximum of four years, and that four years counts time already spent on the Graduate route and on any previous Tier 2 or Skilled Worker permission. The new entrant discount also cannot be combined with the PhD discounts — one option applies, not both. A scholar who spent two years on the Graduate route has two years of new entrant eligibility left, and the salary required at the end of it is materially higher than at the start.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          What sponsorship costs the institution
        </Heading>
        <BodyText className="mb-4">
          The Immigration Skills Charge rose by 32% on 16 December 2025. It is now £1,320 a year for medium and large sponsors and £480 a year for small and charitable sponsors, charged up front for the full period of sponsorship.
        </BodyText>
        <BodyText className="mb-6">
          Higher education is substantially insulated from that increase. Under the amending regulations that took effect on the same date, several academic occupation codes are exempt from the charge altogether — including SOC 2311, higher education teaching professionals; SOC 2119, natural and social science professionals not elsewhere classified; SOC 2161, research and development managers; and SOC 2162, researchers in unspecified disciplines. The exemption also applies where a scholar switches into sponsored work from a Student visa. For an institution recruiting into a research or teaching post, the charge that dominates most sponsorship budgets frequently does not apply at all.
        </BodyText>
        <BodyText className="mb-6">
          The exemption does not track seniority, and it does not cover every code a researcher might be assigned to. The discipline-specific science codes — 2111, 2112, 2113 and 2114 — are not on the exempt list, while the generic research code 2162 is. Two postdoctoral positions of identical grade and duration can therefore differ by several thousand pounds in charge over a three-year appointment purely on the code recorded against them. For a five-year appointment at the large sponsor rate, that is £6,600 that one post incurs and another does not.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Global Talent as the alternative
        </Heading>
        <BodyText className="mb-4">
          Global Talent sits outside the sponsorship system. It requires no Certificate of Sponsorship, no occupation code, no salary threshold and no institution to hold a licence on the scholar's behalf. For academic and research roles it is endorsed by UK Research and Innovation, the Royal Society, the Royal Academy of Engineering and the British Academy, and the endorsed funder route can be quick where a scholar is named on a live grant. The endorsement fee is £524 and is waived for some UKRI-funded researchers.
        </BodyText>
        <BodyText className="mb-6">
          The practical effect is that the salary arithmetic above disappears. A scholar on Global Talent can be appointed to a Grade 6 or Grade 7 post at the advertised salary without reference to a going rate, and the institution incurs no Immigration Skills Charge and no sponsor duties for that person. Where a scholar is plausibly endorsable, working out whether they qualify is usually a better use of time than negotiating a salary upward to clear Option A.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Settlement and the Graduate route
        </Heading>
        <BodyText className="mb-4">
          Two changes affect the shape of an academic career on a visa rather than the appointment itself. The qualifying period for indefinite leave to remain is moving from five years to a baseline of ten for work routes and their dependants, following the May 2025 immigration white paper. That doubles the period during which a scholar's right to remain depends on continuous sponsorship — a consideration that bears directly on whether a fixed-term contract is a reasonable thing to accept.
        </BodyText>
        <BodyText className="mb-6">
          The Graduate route is being cut from two years to 18 months with effect from 1 January 2027. Applications made on or before 31 December 2026 still receive the full two years, and PhD graduates retain three years either way. For anyone finishing a taught or research master's this year, the difference between applying before and after the new year is six months of unsponsored time in which to find a post — and, because Graduate route time counts against the four-year new entrant clock, six months that also shortens the window in which the lowest salary option is available.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Checks worth making before the offer stage
        </Heading>
        <BodyText className="mb-4">
          Most sponsorship problems in higher education surface late, after a scholar has been selected and the salary is already fixed. These are the questions that resolve them earlier:
        </BodyText>
        <BulletList
          marker="dash"
          className="mb-6"
          items={[
            "Which occupation code will the role be assigned to, and what is its Table 1 going rate — a research post and a teaching post can carry very different figures",
            "Which salary option is being relied on, and does the scholar actually satisfy it",
            "How much of the four-year new entrant allowance has already been used, including Graduate route time",
            "Whether the role is exempt from the Immigration Skills Charge under the December 2025 regulations",
            "Whether Global Talent is open to the scholar, which removes the going rate question entirely",
            "For fixed-term appointments, how the contract end date interacts with a ten-year settlement clock",
          ]}
        />
        <BodyText>
          Institutions that publish the occupation code and the salary option alongside a listing tend to lose fewer international scholars at the offer stage, because the arithmetic is settled before either side has invested in the process. Scholars reading a listing without that information can usually reconstruct it from the salary and the grade, and it is worth doing before an application rather than after an offer.
        </BodyText>
      </>
    ),
  },
  {
    slug: "uk-academic-contract-types-explained",
    title: "UK Academic Contract Types Explained: Fixed-Term, Teaching-Only, Teaching and Research",
    excerpt:
      "Listings name contract types without defining them. What teaching-only, research-only and teaching-and-research actually mean for pay, progression and security — with the pay spine figures behind each.",
    category: "Career Tips",
    readTime: "7 min read",
    icon: BookOpen,
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=450&fit=crop",
    author: "Acanetic Editorial Team",
    publishDate: "7 August 2026",
    content: (
      <>
        <BodyText className="mb-6">
          A UK academic contract carries three variables that are set independently of one another: what you are paid to do, how long the contract runs, and where you sit on the pay spine. Listings routinely name all three and define none of them. The three are worth separating, because a teaching-only post can be open-ended and well paid while a teaching-and-research post can be fixed-term and sit two grades lower.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Function: what the contract pays you to do
        </Heading>
        <BodyText className="mb-4">
          HESA records every academic contract against one of three employment functions. In the 2024/25 staff record, covering staff in post on 1 December 2024, the split was:
        </BodyText>
        <BulletList
          marker="disc"
          className="mb-6"
          items={[
            "Teaching and research — 43% of academic staff",
            "Teaching only — 35%",
            "Research only — 21%",
          ]}
        />
        <BodyText className="mb-6">
          The same release recorded the first fall in the total number of academic staff in UK higher education since the series began. The composition matters as much as the total: teaching-only contracts have expanded markedly over the past decade while the share on teaching and research has contracted, which means the modal academic post is no longer the one most doctoral training implicitly prepares people for.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Teaching and research
        </Heading>
        <BodyText className="mb-4">
          This is the contract most people mean by a lectureship. It obliges you to do both, and you are appraised and promoted against both. The practical difficulty is that the two halves are assessed on different cycles by different mechanisms — teaching through student outcomes and internal review, research through outputs and grant income — and the research half is the one attached to institutional funding through the Research Excellence Framework.
        </BodyText>
        <BodyText className="mb-6">
          Workload allocation models are agreed locally rather than nationally, so the notional split between teaching, research and administration varies between institutions and often between departments. Ask for the model in writing. A teaching-and-research contract with no protected research time is a teaching-only contract with a research expectation attached, and it is a materially worse position than either done honestly.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Teaching-only and teaching and scholarship
        </Heading>
        <BodyText className="mb-4">
          Teaching-only contracts remove the research obligation. Many institutions title them teaching and scholarship, where scholarship means keeping current in the discipline and, often, pedagogic research — activity that develops your teaching rather than activity submitted for research assessment. Whether that scholarship comes with protected time is a local question and one of the more useful things to establish before accepting.
        </BodyText>
        <BodyText className="mb-6">
          Two things are worth knowing about this track. The first is that promotion routes for education-focused staff now exist at most institutions, up to and including chairs, which was not reliably true a decade ago. The second is that these routes are newer and thinner than the research routes, and their criteria are frequently less specific — which cuts both ways at promotion. Ask to see the criteria and ask how many people have been promoted under them.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Research-only
        </Heading>
        <BodyText className="mb-6">
          Research-only contracts cover postdoctoral positions and most grant-funded research staff. They are fixed-term far more often than the other two functions, for a structural reason rather than a discretionary one: the post is funded from a grant with an end date, and the contract is written to that date. This is the category where the duration variable and the function variable are most tightly coupled, and where the four-year rule below does most of its work.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Duration: fixed-term and open-ended
        </Heading>
        <BodyText className="mb-4">
          On 1 December 2024, 29% of academic staff were on fixed-term contracts — the same proportion as the year before. The figure has been broadly stable rather than improving.
        </BodyText>
        <BodyText className="mb-4">
          The Fixed-term Employees (Prevention of Less Favourable Treatment) Regulations 2002 set the outer limit. Where you have been continuously employed on successive fixed-term contracts for four years or more, your contract is deemed permanent unless the institution can objectively justify continuing on a fixed-term basis. The right is not automatic in practice: you can request written confirmation from your department that the contract has become permanent, and the burden of demonstrating objective justification sits with the institution rather than with you.
        </BodyText>
        <BodyText className="mb-6">
          A third category sits between the two and is easy to misread on a listing. Open-ended contracts funded from external grants are described as open-ended, and legally they are, but they carry a funding-linked end provision — when the grant stops, a redundancy process follows. This is not the same as a fixed-term contract and it is not the same as a permanent post funded from core income. If a listing says open-ended, establish which of the two it means.
        </BodyText>
        <BodyText className="mb-4">
          What happens at the end of a fixed-term contract is also worth understanding in advance, because the expiry of a fixed term is a dismissal in law rather than a natural conclusion. That brings consequences that many people on these contracts do not realise apply to them:
        </BodyText>
        <BulletList
          marker="check"
          className="mb-6"
          items={[
            "Continuous service accrues across successive contracts with the same institution, including where there are short gaps between them",
            "With two years of continuous service, the ordinary protection against unfair dismissal applies at expiry",
            "Statutory redundancy pay becomes available at two years of continuous service, and most institutions operate more generous contractual terms",
            "Redeployment procedures typically apply, and are usually triggered by a defined period before the end date rather than on request",
          ]}
        />
        <BodyText className="mb-6">
          The practical implication is that the end date is a process date rather than a cliff, and that the process usually begins earlier than people expect. Establishing the redeployment window at the point of signing is more useful than discovering it in the final month.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Grade: what each contract pays
        </Heading>
        <BodyText className="mb-4">
          Pay is set on a single national spine negotiated through New JNCHES. The spine point values are national; the grade boundaries drawn on top of them are set by each institution, so the same spine point can be Grade 7 at one and Grade 8 at another. On the values effective 1 August 2025, the relevant points run:
        </BodyText>
        <BulletList
          marker="disc"
          className="mb-6"
          items={[
            "Spine point 22 — £31,236, a typical research assistant entry point",
            "Spine point 28 — £37,694, a common Grade 7 floor for lecturers and research fellows",
            "Spine point 30 — £41,064",
            "Spine point 34 — £47,389, frequently the Grade 7 ceiling and the Grade 8 floor",
            "Spine point 39 — £58,225, around the Grade 8 ceiling",
            "Spine point 45 — £69,488, a common professorial minimum",
          ]}
        />
        <BodyText className="mb-6">
          The 2025/26 round applied a 4.0% uplift to most spine points, with larger increases at the lowest points to hold a real living wage floor. For 2026/27, effective 1 August 2026, the offer on the table has been 1.8% across the spine with 2% at spine point 7. London weighting is paid on top and is set institutionally, typically in the low thousands.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Pension: which scheme depends on the institution, not the role
        </Heading>
        <BodyText className="mb-4">
          Pension is the largest component of the package after salary and it is not negotiable, because it follows the institution rather than the post. Pre-1992 institutions generally participate in the Universities Superannuation Scheme; post-1992 institutions generally use the Teachers' Pension Scheme. Moving between the two sectors means changing scheme, and the schemes are structurally different.
        </BodyText>
        <BodyText className="mb-6">
          USS is a funded hybrid scheme run across multiple institutions, providing defined benefits up to a salary threshold and defined contributions above it, with defined benefits currently accruing at 1/75th of salary a year. Member contributions are 6.1% of salary against an institutional contribution of 14.5%, with the 2026 valuation due to set the rates payable from 2027. The Teachers' Pension Scheme is an unfunded public service scheme financed on a pay-as-you-go basis, accruing at 1/57th a year, with member contributions tiered by salary band. The higher accrual rate does not by itself make one scheme better than the other — the thresholds, indexation and investment elements differ — but the difference is large enough to be worth modelling before moving between sectors, particularly mid-career.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Progression: increments and promotion are different things
        </Heading>
        <BodyText className="mb-4">
          Within a grade you move up one spine point a year, usually on the anniversary of appointment or on a common date. This is automatic in most institutions and is not a reward for performance. It stops at the top of the grade.
        </BodyText>
        <BodyText className="mb-6">
          After that, the only route to higher pay is promotion to the next grade, which runs on an annual cycle against published criteria and is not automatic in any institution. The gap between the two mechanisms is where most pay stagnation happens: a lecturer appointed at spine point 28 reaches the Grade 7 ceiling in six years and then stops entirely until promoted. New appointments also usually carry probation, commonly with a teaching qualification requirement attached, and probation criteria are worth reading before signing rather than at first review.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          What to establish before accepting
        </Heading>
        <BulletList
          marker="dash"
          className="mb-6"
          items={[
            "The employment function on the contract, not the job title — teaching-only posts are sometimes advertised as lectureships",
            "Whether an open-ended contract is core-funded or carries a funding-linked end provision",
            "The workload allocation model, and whether research or scholarship time is protected within it",
            "The grade, the spine point of appointment, and the spine point at which the grade stops",
            "The promotion criteria for your track, and how recently anyone was promoted under them",
            "The probation period, its criteria, and whether a teaching qualification is required within it",
            "For fixed-term contracts, how much continuous service you will have accrued at the end date",
          ]}
        />
        <BodyText>
          The three variables are negotiated separately, which also means they can be negotiated separately. Spine point on appointment is the most commonly movable of the three, and the least commonly asked about.
        </BodyText>
      </>
    ),
  },
  {
    slug: "ref-2029-what-decoupling-changes-for-recruitment",
    title: "REF 2029 and Recruitment: What Decoupling Outputs from Staff Changes for Institutions",
    excerpt:
      "REF 2029 no longer collects staff details, and submission volume is set by average FTE across 2025/26 and 2026/27. Both years are now in scope, which puts current recruitment decisions inside the measurement window.",
    category: "For Institutions",
    readTime: "8 min read",
    icon: GraduationCap,
    image: "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&h=450&fit=crop",
    author: "Acanetic Editorial Team",
    publishDate: "11 August 2026",
    content: (
      <>
        <BodyText className="mb-6">
          The Research Excellence Framework has shaped UK academic recruitment for as long as it has existed, mostly through a simple mechanism: outputs belonged to individuals, individuals could be recruited, and so outputs could be recruited. REF 2029 removes that mechanism. Institutions will no longer submit staff details at all, and the volume an institution must submit is derived from workforce data covering 2025/26 and 2026/27 — two years that are now respectively closed and in progress.
        </BodyText>

        <BodyText className="mb-6">
          The stakes are unchanged by the restructuring. REF outcomes inform the allocation of roughly £2 billion a year of public research funding across the UK, of which Research England's mainstream quality-related allocation accounts for around £1.3 billion a year in England alone. Because the outcomes are periodic, that money is unusually stable between exercises — which is what makes each exercise consequential, and what makes the workforce decisions feeding into it worth taking deliberately.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          The three elements and their weightings
        </Heading>
        <BodyText className="mb-4">
          REF 2029 replaces the outputs, impact and environment structure with three renamed elements. The weightings were revised in December 2025, after consultation, and now stand at:
        </BodyText>
        <BulletList
          marker="disc"
          className="mb-6"
          items={[
            "Contributions to Knowledge and Understanding — 55%, replacing the outputs element",
            "Engagement and Impact — 25%, carrying the impact case studies",
            "Strategy, People and Research Environment — 20%, replacing the environment element",
          ]}
        />
        <BodyText className="mb-6">
          The third element is the one that moved. It was proposed at 25% under the name People, Culture and Environment, and was cut to 20% and renamed in the December 2025 revision, with the five points transferred to Contributions to Knowledge and Understanding. Institutions that built a REF strategy against the earlier consultation figures are working from superseded weightings.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Outputs are no longer attached to named staff
        </Heading>
        <BodyText className="mb-4">
          This is the structural change. Institutions do not submit staff details, and outputs are not tied to a submitted author — they are submitted to the unit of assessment where they best fit. There is no minimum number of outputs required from any individual, and the earlier proposal to remove the maximum was reversed: a recommended ceiling of five outputs per researcher was reintroduced in December 2025, matching REF 2021, on the reasoning that a decoupled framework needs guard rails to prevent a small number of people carrying a submission.
        </BodyText>
        <BodyText className="mb-6">
          What replaces individual accounting is a representativeness test. A submission must demonstrate that it reflects the breadth of research activity and strategy in the unit, rather than a selected subset of it. That is a different exercise from identifying the strongest outputs and the people attached to them, and it rewards a different kind of preparation.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          The substantive link is an employment threshold
        </Heading>
        <BodyText className="mb-4">
          Decoupling does not sever the connection between institutions and outputs entirely. Every output submitted must have a substantive link to the institution submitting it, and that link is defined in employment terms: an eligible employment relationship with an author who made a significant research contribution to the output, where eligible employment means a minimum of 0.2 FTE for at least twelve months of continuous employment.
        </BodyText>
        <BodyText className="mb-6">
          That threshold is low, and it is the most directly actionable number in the framework. A one-day-a-week appointment held for a year establishes eligibility; eleven months at full time does not. Fractional and visiting appointments, which many institutions treat as peripheral, sit squarely inside the definition, while short-term cover and sub-0.2 arrangements sit outside it regardless of how productive they are. Institutions that use fractional contracts extensively should know which of them cross the line, and scholars negotiating a fractional appointment should know that the difference between 0.15 and 0.2 FTE is not merely proportional.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          The volume measure puts current hiring inside the window
        </Heading>
        <BodyText className="mb-4">
          Institutions must submit 2.5 outputs for every full-time equivalent researcher. The FTE figure is not taken on a census date, as in previous exercises, but as an average across two academic years — 2025/26 and 2026/27 — drawn from the HESA staff record rather than a separate REF return.
        </BodyText>
        <BodyText className="mb-6">
          The timing is the point. The 2025/26 contribution is fixed and cannot now be influenced. The 2026/27 year began on 1 August 2026 and is being determined by decisions being taken now. Every research FTE added during this year raises the average, and therefore raises the number of outputs the institution is obliged to submit — at 2.5 outputs per FTE, an appointment made this year carries a submission obligation attached to it. That is not an argument against recruiting, but it is an argument for recruiting people whose output profile can carry the requirement they create, and for knowing the current position before approving a round of listings.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          What decoupling does to the transfer market
        </Heading>
        <BodyText className="mb-4">
          Under previous exercises, recruiting a scholar shortly before the census date could move outputs from one submission to another, and the market behaved accordingly. Decoupling substantially removes that return. Outputs are assessed against the institution where the research was done rather than travelling with the person who did it, under a substantive link policy.
        </BodyText>
        <BodyText className="mb-6">
          One exception survives and is worth knowing precisely: long-form and extended-process outputs remain portable for five years. In monograph-based disciplines the older logic therefore still applies to a degree, and in most others it does not. Recruitment built on late-cycle acquisition of individual outputs has a much narrower field of application than it did in 2021.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Open access applies from the start of 2026
        </Heading>
        <BodyText className="mb-6">
          The REF 2029 open access policy took effect on 1 January 2026 and applies to journal articles and conference papers carrying an ISSN. Compliance is established at the point of publication rather than at submission, which means outputs produced now are already inside the policy period and outputs that fail it cannot be remediated in 2028. For recruitment this converts an administrative requirement into a due diligence question: where a scholar joins mid-cycle, the compliance status of the work they produced elsewhere during the period is a property of that work already, and it travels with the output rather than with the person. Institutions bringing in staff from organisations with weaker deposit infrastructure should check rather than assume.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Engagement and Impact scales with the size of the unit
        </Heading>
        <BodyText className="mb-4">
          The impact element keeps its 25% weighting but changes shape. The minimum number of case studies per submission falls from two to one, and the number required is set by the average FTE of the staff contributing to the volume measure in that unit — the same workforce figure that determines output volume. The 2* quality threshold on the research underpinning a case study is removed.
        </BodyText>
        <BodyText className="mb-6">
          A structured statement will account for at least 20% of the quality sub-profile for this element, weighted on a sliding scale so that its contribution is never less than that of a single case study. The effect is to make the element proportionately more demanding for small units, where one statement and one case study carry the whole assessment, and to tie impact planning to the same headcount decisions that drive everything else in the exercise.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          Strategy, People and Research Environment is a recruitment element
        </Heading>
        <BodyText className="mb-6">
          At 20% of the total, the element that assesses research environment now carries more weight than the environment element did in previous exercises, and it assesses things that recruitment practice directly produces: how research careers are supported, how staff are developed, and the conditions people are employed under. An institution with 29% of its academic staff on fixed-term contracts — the sector figure on 1 December 2024 — is describing its research environment when it publishes those contracts, whether or not it intends to. Contract structure, probation design and career development provision are evidence for this element, and they are decided in recruitment rather than in the writing-up phase.
        </BodyText>
        <BodyText className="mb-6">
          This is the element where the decoupled framework has the clearest internal logic. Once outputs are no longer credited to named individuals, the question the exercise is asking shifts from whether an institution has assembled strong researchers to whether it has built somewhere strong research happens. Those are answered by different evidence. The first is answered by a recruitment record; the second is answered by retention, by progression from fixed-term to open-ended posts, and by whether early career staff who arrive are still there and advancing four years later. An institution can improve its position on this element without appointing anyone, which was not true of any element in previous exercises.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          The timetable
        </Heading>
        <BulletList
          marker="check"
          className="mb-6"
          items={[
            "2026 — expert panels meet and develop the assessment criteria and working methods",
            "2027 — survey of submission intentions",
            "Autumn 2028 — submission deadline, with assessment beginning after it",
            "December 2029 — results published",
          ]}
        />
        <BodyText className="mb-6">
          The two years that set the volume measure both fall before the criteria are finalised. Institutions are therefore fixing the size of their obligation before the full basis on which it will be judged is published — which is an argument for treating the volume measure as the near-term planning constraint and the criteria as the medium-term one.
        </BodyText>

        <Heading level={3} className="mb-4 mt-8">
          What this changes about listings now
        </Heading>
        <BulletList
          marker="dash"
          className="mb-6"
          items={[
            "Establish the unit's current 2025/26 FTE average and the obligation it has already created before opening a round",
            "Treat each research FTE appointed this year as carrying 2.5 outputs of submission obligation",
            "Assess scholars on the research they will produce in post rather than on outputs they bring, outside long-form disciplines",
            "Describe contract type, probation and development provision in the listing — these are the evidence base for a fifth of the assessment",
            "Check whether a strategy written against the 25% culture weighting needs revising to the 20% figure",
          ]}
        />
        <BodyText>
          The exercise has moved from assessing which people an institution employs to assessing what an institution does with them. Recruitment is still the lever, but it operates on the conditions of employment rather than on the acquisition of outputs.
        </BodyText>
      </>
    ),
  },
];

export function getArticleBySlug(slug: string): BlogArticle | undefined {
  return blogArticles.find((article) => article.slug === slug);
}

export function getRelatedArticles(currentSlug: string, limit: number = 3): BlogArticle[] {
  const current = getArticleBySlug(currentSlug);
  if (!current) return blogArticles.slice(0, limit);

  const sameCategory = blogArticles.filter(
    (a) => a.slug !== currentSlug && a.category === current.category
  );
  const otherCategory = blogArticles.filter(
    (a) => a.slug !== currentSlug && a.category !== current.category
  );

  return [...sameCategory, ...otherCategory].slice(0, limit);
}
